import mapboxgl from 'mapbox-gl';

/**
 * BaseMapbox
 * Base utility class for Mapbox map components
 * Provides shared functionality for map initialization, error handling, and utilities
 */
var BaseMapbox = {};

/**
 * Parse map items from data attribute
 * 
 * @param {jQuery} $el - jQuery element containing the data attribute
 * @param {string} componentName - Component name for logging
 * @returns {Array} Array of map items
 */
BaseMapbox.parseMapItems = function($el, componentName) {
	var mapItemsData = $el.attr('data-map-items');
	if (mapItemsData) {
		try {
			return JSON.parse(mapItemsData);
		} catch (e) {
			console.warn(componentName + ': Failed to parse map items data', e);
			return [];
		}
	}
	return [];
};

/**
 * Calculate initial center and zoom from map items with coordinates
 * 
 * @param {Array} mapItems - Array of map items
 * @param {Array<number>} defaultCenter - Default center [lng, lat]
 * @param {number} defaultZoom - Default zoom level
 * @returns {Object} Object with center and zoom properties
 */
BaseMapbox.calculateInitialCenterAndZoom = function(mapItems, defaultCenter, defaultZoom) {
	var initialCenter = defaultCenter;
	var initialZoom = defaultZoom;
	
	if (mapItems && mapItems.length > 0) {
		// Get all items that already have coordinates
		var itemsWithCoords = mapItems.filter(function(item) {
			return item.coordinates && item.coordinates.length === 2;
		});
		
		if (itemsWithCoords.length > 0) {
			// Calculate bounds from existing coordinates
			var bounds = new mapboxgl.LngLatBounds();
			itemsWithCoords.forEach(function(item) {
				bounds.extend(item.coordinates);
			});
			
			// Get center from bounds
			var center = bounds.getCenter();
			initialCenter = [center.lng, center.lat];
			
			// Set a wider zoom initially (will be refined after all markers are added)
			initialZoom = 12;
		}
	}
	
	return {
		center: initialCenter,
		zoom: initialZoom
	};
};

/**
 * Setup map error handling for style layer errors
 * 
 * @param {mapboxgl.Map} map - Mapbox map instance
 * @param {Function} removeProblematicLayerFn - Function to call to remove problematic layers (bound to component instance)
 * @param {string} componentName - Component name for logging
 */
BaseMapbox.setupMapErrorHandling = function(map, removeProblematicLayerFn, componentName) {
	// Handle style errors (e.g., missing layers like hillshade)
	map.on('error', function(e) {
		if (e.error && e.error.message) {
			if (e.error.message.indexOf('hillshade') !== -1 || 
				e.error.message.indexOf('does not exist on source') !== -1) {
				console.warn(componentName + ': Style layer error detected:', e.error.message);
				// Try to remove problematic layer after style loads
				map.once('style.load', function() {
					removeProblematicLayerFn('hillshade');
				});
			} else {
				console.error(componentName + ': Map error', e.error);
			}
		}
	});

	// Handle style loading - remove problematic layers proactively
	map.on('style.load', function() {
		removeProblematicLayerFn('hillshade');
	});
};

/**
 * Remove a problematic layer from the map style
 * Used to handle style errors where a layer references a missing source
 * 
 * @param {mapboxgl.Map} map - Mapbox map instance
 * @param {string} layerId - The ID of the layer to remove
 * @param {string} componentName - Component name for logging
 */
BaseMapbox.removeProblematicLayer = function(map, layerId, componentName) {
	try {
		var layer = null;
		try {
			layer = map.getLayer(layerId);
		} catch (err) {
			// Layer doesn't exist, which is fine
			return;
		}
		
		if (layer) {
			try {
				// Check if the source exists
				var sourceId = layer.source;
				if (sourceId) {
					try {
						var source = map.getSource(sourceId);
						if (!source) {
							// Source doesn't exist, remove the layer
							map.removeLayer(layerId);
						}
					} catch (sourceErr) {
						// Source doesn't exist, remove the layer
						map.removeLayer(layerId);
					}
				}
			} catch (err) {
				// If we can't check source, try to remove layer anyway
				try {
					map.removeLayer(layerId);
				} catch (removeErr) {
					// Layer might not be removable
					console.warn(componentName + ': Could not remove layer', layerId, removeErr);
				}
			}
		}
	} catch (err) {
		// Error is non-fatal, map will still work
		console.warn(componentName + ': Error checking style layers', err);
	}
};

/**
 * Setup window resize handler for map
 * 
 * @param {mapboxgl.Map} map - Mapbox map instance
 * @returns {Function} Cleanup function to remove the event listener
 */
BaseMapbox.setupMapResize = function(map) {
	var resizeHandler = function() {
		if (map) {
			map.resize();
		}
	};
	
	window.addEventListener('resize', resizeHandler);
	
	// Return cleanup function
	return function() {
		window.removeEventListener('resize', resizeHandler);
	};
};

/**
 * Fit map bounds to show all coordinates
 * 
 * @param {mapboxgl.Map} map - Mapbox map instance
 * @param {Array} coordinates - Array of coordinate arrays [[lng, lat], ...]
 * @param {Object} options - Options for fitBounds
 * @param {Object} options.padding - Padding object {top, bottom, left, right}
 * @param {number} options.maxZoom - Maximum zoom level
 * @param {number} options.duration - Animation duration in ms
 * @param {number} options.singleMarkerZoom - Zoom level for single marker
 */
BaseMapbox.fitMapBounds = function(map, coordinates, options) {
	options = options || {};
	var padding = options.padding || { top: 50, bottom: 50, left: 50, right: 50 };
	var maxZoom = options.maxZoom || 15;
	var duration = options.duration || 1000;
	var singleMarkerZoom = options.singleMarkerZoom || 14;
	
	if (!coordinates || coordinates.length === 0) {
		console.warn('BaseMapbox: No coordinates to fit bounds to');
		return;
	}

	// Create bounds object
	var bounds = new mapboxgl.LngLatBounds();

	// Extend bounds to include each coordinate
	coordinates.forEach(function(coord) {
		if (coord && coord.length === 2) {
			bounds.extend(coord);
		}
	});

	// If only one coordinate, center on it with a reasonable zoom level
	if (coordinates.length === 1) {
		map.setCenter(coordinates[0]);
		map.setZoom(singleMarkerZoom);
		return;
	}

	// Fit map to bounds with padding
	map.fitBounds(bounds, {
		padding: padding,
		maxZoom: maxZoom,
		duration: duration
	});
};

export default BaseMapbox;

